import React from 'react';
import { makeStyles } from '@mui/styles';
import { AccordionDetails, AccordionSummary, Grid, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ParamTable from '../ParamTable';
import { CodeHighlighter } from '../index';
import { DashboardScreen } from '../../../DashboardScreen';

const useStyles = makeStyles((theme) => ({
  pre: {
    whiteSpace: 'pre-wrap',
  },
  childAccordion: {
    display: 'block',
  },
  headingPost: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
    color: theme.palette.success.main,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
}));
const DietsDietCodeQuality = () => {
  const classes = useStyles();
  return (
    <AccordionDetails className={classes.childAccordion}>
      <Typography>
        <Grid
          container
          item
          xs={12}
          spacing={2}
          justifyContent={'space-between'}
        >
          <Grid
            item
            xs={2}
          >
            <h3>Example Request</h3>
          </Grid>
          <Grid
            item
            xs={10}
          >
            <ParamTable
              rows={[
                { name: 'diet_code', type: 'string', description: 'The short code of a Diet ID Diet found via /diets' },
                { name: 'quality', type: 'integer', description: 'The quality level of a Diet ID Diet.' },
              ]}
            />
          </Grid>
          <Grid
            item
            xs={2}
          >
            <h3>URL Params</h3>
          </Grid>
          <Grid
            item
            xs={10}
          >
            <ParamTable
              rows={[
                {
                  name: 'workflowID',
                  type: 'integer',
                  description: "Adjust the Diet's Food Group Values by a Patient's specific Workflow ID.",
                },
              ]}
            />
          </Grid>
          <Grid
            item
            xs={2}
          >
            <h3>Example Response</h3>
          </Grid>
          <Grid
            item
            xs={10}
          >
            <CodeHighlighter
              code={`{
    "data": {
        "id": 1,
        "name": "American",
        "code": "AME",
        "quality": 1,
        "longName": "American quality 1 (1 out of 10)",
        "description": "This dietary pattern typically includes highly processed meals and snacks, fried foods, packaged/convenience foods, sweetened beverages, sweets, and fast food.",
        "hei": 17,
        "calories": 2067.000333333333,
        "adjustedCalories": "",
        "fingerprintPhotoUrl": "https://dqpn.imgix.net/assets/diet-images/1_AME/1/fingerprint_photo_zQmjRnFnuYOjexPNOMBehQwloaglso.png?w=960&auto=format,compression&txtalign=bottom%2Cright&txtclr=c1cdbd&txt=%C2%A9%202022%20Diet%20ID%20Inc&txtsize=36&txtfont=Arial%2CBoldMT&txtpad=15&txtalign=center",
        "foodGroupValues": {
            "FRU0100": 0.0,
            "FRU0200": 0.0,
            "FRU0300": 0.0,
            "FRU0400": 0.0,
            "FRU0500": 0.0,
            "FRU0600": 0.0,
            "FRU0700": 0.0,
            "VEG0100": 0.0,
            "VEG0200": 0.0,
            "VEG0300": 0.2083333333333333,
            "VEG0400": 0.1946666666666667,
            "VEG0800": 0.357,
            "VEG0450": 0.0,
            "VEG0700": 0.0,
            "VEG0600": 0.029,
            "VEG0900": 0.0,
            "VEG0500": 0.0,
            "FMC0100": 0.0,
            "GRW0100": 0.0,
            "GRS0100": 0.0,
            "GRR0100": 1.176333333333333,
            "GRW0200": 0.0,
            "GRS0200": 0.0,
            "GRR0200": 2.341,
            "GRW0300": 0.0,
            "GRS0300": 0.0,
            "GRR0300": 0.0,
            "GRW0400": 0.0,
            "GRS0400": 0.0,
            "GRR0400": 0.0,
            "GRW0500": 0.0,
            "GRS0500": 0.0,
            "GRR0500": 0.381,
            "GRW0600": 0.0,
            "GRS0600": 0.0,
            "GRR0600": 0.0,
            "GRW0700": 0.0,
            "GRS0700": 0.0,
            "GRR0700": 0.0,
            "GRW0800": 0.0,
            "GRS0800": 0.0,
            "GRR0800": 2.432666666666667,
            "GRW1000": 0.0,
            "GRS1000": 0.0,
            "GRR1000": 0.0,
            "GRW0900": 0.0,
            "GRS0900": 0.0,
            "GRR0900": 0.0,
            "GRW1100": 0.0,
            "GRW1200": 0.0,
            "GRR1300": 0.0,
            "MRF0100": 0.9756666666666667,
            "MRL0100": 0.0,
            "MRF0200": 0.0,
            "MRL0200": 0.0,
            "MRF0300": 0.0,
            "MRL0300": 0.0,
            "MRF0400": 0.0,
            "MRL0400": 0.0,
            "MCF0200": 0.2546666666666667,
            "MCL0200": 0.0,
            "MRF0500": 0.0,
            "MPF0100": 0.0,
            "MPL0100": 0.0,
            "MPF0200": 1.27,
            "MFF0100": 0.0,
            "MFL0100": 0.0,
            "MFF0200": 0.0,
            "MSL0100": 0.0,
            "MSF0100": 0.0,
            "MCF0100": 1.723,
            "MCL0100": 0.06666666666666667,
            "MOF0100": 0.0,
            "MOF0200": 0.0,
            "FMC0200": 0.0,
            "MOF0300": 0.0,
            "MOF0400": 0.0,
            "MOF0500": 0.0,
            "MOF0600": 0.0,
            "MOF0700": 0.005999999999999999,
            "DMF0100": 0.0,
            "DMR0100": 0.0,
            "DML0100": 0.0,
            "DMN0100": 0.006333333333333333,
            "DMF0200": 0.0,
            "DMR0200": 0.0,
            "DML0200": 0.0,
            "DML0300": 0.0,
            "DML0400": 0.0,
            "SWT0600": 0.0,
            "MSC1100": 0.0,
            "DCF0100": 0.1646666666666667,
            "DCR0100": 0.3606666666666667,
            "DCL0100": 0.0,
            "DCN0100": 0.0,
            "DYF0100": 0.0,
            "DYR0100": 0.0,
            "DYL0100": 0.0,
            "DYF0200": 0.0,
            "DYR0200": 0.0,
            "DYL0200": 0.0,
            "DYN0100": 0.0,
            "DOT0100": 0.0,
            "DOT0200": 0.2913333333333333,
            "DOT0300": 0.0,
            "DOT0400": 0.0,
            "FCF0100": 0.2423333333333333,
            "FCR0100": 0.0,
            "FCL0100": 0.0,
            "FCN0100": 2.666666666666667,
            "DOT0500": 0.0,
            "DOT0600": 0.0,
            "DOT0700": 0.0,
            "DOT0800": 0.0,
            "FMF0100": 0.0,
            "FMR0100": 0.0,
            "FOF0100": 0.9580000000000001,
            "FSF0100": 0.1816666666666667,
            "FAF0100": 1.666666666666667,
            "FAR0100": 0.0,
            "FDF0100": 0.0,
            "FDR0100": 0.0,
            "SWT0400": 3.555333333333333,
            "MSC1200": 0.0,
            "SWT0500": 0.1516666666666667,
            "SWT0700": 0.0,
            "SWT0800": 0.0,
            "SWT0100": 0.1333333333333333,
            "SWT0200": 0.7236666666666666,
            "SWT0300": 0.0,
            "BVS0400": 2.5,
            "BVA0400": 0.5,
            "BVU0300": 0.0,
            "BVS0300": 0.0,
            "BVA0300": 0.0,
            "BVS0500": 0.0,
            "BVA0500": 0.0,
            "BVU0400": 0.0,
            "BVS0100": 0.0,
            "BVA0100": 0.0,
            "BVU0100": 0.6516666666666667,
            "BVS0200": 0.0,
            "BVA0200": 0.0,
            "BVU0200": 0.0,
            "BVS0600": 0.0,
            "BVA0600": 0.0,
            "BVU0500": 0.0,
            "BVS0700": 0.0,
            "BVA0700": 0.0,
            "BVU0600": 0.0,
            "BVO0100": 0.0,
            "BVO0200": 0.0,
            "BVE0100": 0.3333333333333333,
            "BVE0400": 0.0,
            "BVE0300": 0.0,
            "BVE0200": 0.0,
            "MSC0100": 0.1666666666666667,
            "MSC0200": 0.0,
            "MSC0300": 0.0,
            "MSC0400": 3.096,
            "MSC0500": 0.07766666666666668,
            "MSC0600": 0.0,
            "MSC0700": 0.0,
            "MSC0800": 0.0,
            "MSC0900": 0.0,
            "MSC1000": 0.0,
            "GRW1300": 0.0,
            "GRS1300": 0.0
        },
        "foodGroupInfo": {
            "FRU0100": {
                "label": "Citrus Juice",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FRU0200": {
                "label": "Fruit Juice excluding Citrus Juice",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FRU0300": {
                "label": "Citrus Fruit",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FRU0400": {
                "label": "Fruit excluding Citrus Fruit",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FRU0500": {
                "label": "Avocados",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FRU0600": {
                "label": "Fried Fruits",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FRU0700": {
                "label": "Fruit-based Savory Snack",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "VEG0100": {
                "label": "Green Leafy Vegetables",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "VEG0200": {
                "label": "Deep-yellow Vegetables",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "VEG0300": {
                "label": "Tomato",
                "daily_values": [
                    0.0,
                    0.625,
                    0.0
                ],
                "value": 0.2083333333333333
            },
            "VEG0400": {
                "label": "White Potatoes",
                "daily_values": [
                    0.0,
                    0.584,
                    0.0
                ],
                "value": 0.1946666666666667
            },
            "VEG0800": {
                "label": "French Fries",
                "daily_values": [
                    1.071,
                    0.0,
                    0.0
                ],
                "value": 0.357
            },
            "VEG0450": {
                "label": "Other Starchy Vegetables",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "VEG0700": {
                "label": "Beans",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "VEG0600": {
                "label": "Other Vegetables",
                "daily_values": [
                    0.087,
                    0.0,
                    0.0
                ],
                "value": 0.029
            },
            "VEG0900": {
                "label": "Fried Vegetables",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "VEG0500": {
                "label": "Vegetable Juice",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FMC0100": {
                "label": "Vegetable Based Savory Snack (Potato Chips)",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRW0100": {
                "label": "Grains, Flour and Dry Mixes - Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRS0100": {
                "label": "Grains, Flour and Dry Mixes - Some Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRR0100": {
                "label": "Grains, Flour and Dry Mixes - Refined Grain",
                "daily_values": [
                    0.0,
                    3.529,
                    0.0
                ],
                "value": 1.176333333333333
            },
            "GRW0200": {
                "label": "Loaf-type Bread and Plain Rolls - Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRS0200": {
                "label": "Loaf-type Bread and Plain Rolls - Some Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRR0200": {
                "label": "Loaf-type Bread and Plain Rolls - Refined Grain",
                "daily_values": [
                    3.023,
                    0.0,
                    4.0
                ],
                "value": 2.341
            },
            "GRW0300": {
                "label": "Quick and Other Breads",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRS0300": {
                "label": "Other Breads (quick breads, corn muffins, tortillas) - Some Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRR0300": {
                "label": "Refined Snack Breads",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRW0400": {
                "label": "Crackers - Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRS0400": {
                "label": "Crackers - Some Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRR0400": {
                "label": "Crackers - Refined Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRW0500": {
                "label": "Pasta - Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRS0500": {
                "label": "Pasta - Some Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRR0500": {
                "label": "Pasta - Refined Grain",
                "daily_values": [
                    1.143,
                    0.0,
                    0.0
                ],
                "value": 0.381
            },
            "GRW0600": {
                "label": "Ready-to-eat Cereal (not presweetened) - Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRS0600": {
                "label": "Ready-to-eat Cereal (not presweetened) - Some Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRR0600": {
                "label": "Ready-to-eat Cereal (not presweetened) - Refined Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRW0700": {
                "label": "Ready-to-eat Cereal (presweetened) - Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRS0700": {
                "label": "Ready-to-eat Cereal (presweetened) - Some Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRR0700": {
                "label": "Ready-to-eat Cereal (presweetened) - Refined Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRW0800": {
                "label": "Whole Grain Cookies, Pies, Pastries, Danish, etc.",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRS0800": {
                "label": "Some Whole Grain Cookies, Pies, Pastries, Danish, etc.",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRR0800": {
                "label": "Cookies, Pies, Pastries, Danish, etc.",
                "daily_values": [
                    2.433,
                    3.128,
                    1.737
                ],
                "value": 2.432666666666667
            },
            "GRW1000": {
                "label": "Whole Grain Snack Bars",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRS1000": {
                "label": "Snack Bars Some Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRR1000": {
                "label": "Snack Bars",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRW0900": {
                "label": "Whole Grain Snack Chips",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRS0900": {
                "label": "Snack Chips Some Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRR0900": {
                "label": "Snack Chips",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRW1100": {
                "label": "Plain Popcorn",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRW1200": {
                "label": "Flavored Popcorn",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRR1300": {
                "label": "Baby Food Grain Mixtures – Refined Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MRF0100": {
                "label": "Beef",
                "daily_values": [
                    2.677,
                    0.25,
                    0.0
                ],
                "value": 0.9756666666666667
            },
            "MRL0100": {
                "label": "Lean Beef",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MRF0200": {
                "label": "Veal",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MRL0200": {
                "label": "Lean Veal",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MRF0300": {
                "label": "Lamb",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MRL0300": {
                "label": "Lean Lamb",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MRF0400": {
                "label": "Fresh Pork",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MRL0400": {
                "label": "Lean Fresh Pork",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MCF0200": {
                "label": "Cured Pork",
                "daily_values": [
                    0.0,
                    0.2,
                    0.564
                ],
                "value": 0.2546666666666667
            },
            "MCL0200": {
                "label": "Lean Cured Pork",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MRF0500": {
                "label": "Game",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MPF0100": {
                "label": "Poultry",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MPL0100": {
                "label": "Lean Poultry",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MPF0200": {
                "label": "Fried Chicken",
                "daily_values": [
                    0.0,
                    3.81,
                    0.0
                ],
                "value": 1.27
            },
            "MFF0100": {
                "label": "Fish",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MFL0100": {
                "label": "Lean Fish",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MFF0200": {
                "label": "Fried Fish",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MSL0100": {
                "label": "Shellfish",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MSF0100": {
                "label": "Fried Shellfish",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MCF0100": {
                "label": "Cold Cuts and Sausage",
                "daily_values": [
                    0.0,
                    0.582,
                    4.587
                ],
                "value": 1.723
            },
            "MCL0100": {
                "label": "Lean Cold Cuts and Sausage",
                "daily_values": [
                    0.0,
                    0.2,
                    0.0
                ],
                "value": 0.06666666666666667
            },
            "MOF0100": {
                "label": "Organ Meats",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MOF0200": {
                "label": "Baby Food Meat Mixtures",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FMC0200": {
                "label": "Meat Based Savory Snack (Pork Rinds)",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MOF0300": {
                "label": "Eggs",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MOF0400": {
                "label": "Egg Substitute",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MOF0500": {
                "label": "Nuts and Seeds",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MOF0600": {
                "label": "Nut and Seed Butters",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MOF0700": {
                "label": "Meat Alternatives",
                "daily_values": [
                    0.0,
                    0.018,
                    0.0
                ],
                "value": 0.005999999999999999
            },
            "DMF0100": {
                "label": "Whole Milk",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DMR0100": {
                "label": "Milk - Reduced Fat",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DML0100": {
                "label": "Milk - Low Fat and Fat Free",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DMN0100": {
                "label": "Milk - Nondairy",
                "daily_values": [
                    0.0,
                    0.019,
                    0.0
                ],
                "value": 0.006333333333333333
            },
            "DMF0200": {
                "label": "Ready-to-drink Flavored Milk - Whole",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DMR0200": {
                "label": "Ready-to-drink Flavored Milk - Reduced Fat",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DML0200": {
                "label": "Ready-to-drink Flavored Milk - Low Fat and Fat Free",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DML0300": {
                "label": "Sweetened Flavored Milk Beverage Powder with Non-fat Dry Milk",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DML0400": {
                "label": "Artificially Sweetened Flavored Milk Beverage Powder with Non-fat Dry Milk",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "SWT0600": {
                "label": "Sweetened Flavored Milk Beverage Powder without Non-fat Dry Milk",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MSC1100": {
                "label": "Artificially Sweetened Flavored Milk Beverage Powder without Non-fat Dry Milk",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DCF0100": {
                "label": "Cheese",
                "daily_values": [
                    0.494,
                    0.0,
                    0.0
                ],
                "value": 0.1646666666666667
            },
            "DCR0100": {
                "label": "Cheese - Reduced Fat",
                "daily_values": [
                    0.0,
                    1.082,
                    0.0
                ],
                "value": 0.3606666666666667
            },
            "DCL0100": {
                "label": "Cheese - Low Fat and Fat Free",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DCN0100": {
                "label": "Cheese - Nondairy",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DYF0100": {
                "label": "Yogurt - Sweetened Whole Milk",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DYR0100": {
                "label": "Yogurt - Sweetened Low Fat",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DYL0100": {
                "label": "Yogurt - Sweetened Fat Free",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DYF0200": {
                "label": "Yogurt - Artificially Sweetened Whole Milk",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DYR0200": {
                "label": "Yogurt - Artificially Sweetened Low Fat",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DYL0200": {
                "label": "Yogurt - Artificially Sweetened Fat Free",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DYN0100": {
                "label": "Yogurt - Nondairy",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DOT0100": {
                "label": "Frozen Dairy Dessert",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DOT0200": {
                "label": "Frozen Nondairy Dessert",
                "daily_values": [
                    0.0,
                    0.874,
                    0.0
                ],
                "value": 0.2913333333333333
            },
            "DOT0300": {
                "label": "Pudding and Other Dairy Dessert",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DOT0400": {
                "label": "Artificially Sweetened Pudding and Other Dairy Dessert",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FCF0100": {
                "label": "Cream",
                "daily_values": [
                    0.727,
                    0.0,
                    0.0
                ],
                "value": 0.2423333333333333
            },
            "FCR0100": {
                "label": "Cream - Reduced Fat",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FCL0100": {
                "label": "Cream - Low Fat and Fat Free",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FCN0100": {
                "label": "Cream - Nondairy, Soy, rice, grain based",
                "daily_values": [
                    4.0,
                    2.0,
                    2.0
                ],
                "value": 2.666666666666667
            },
            "DOT0500": {
                "label": "Dairy-based Sweetened Meal Replacement/Supplement",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DOT0600": {
                "label": "Dairy-based Artificially Sweetened Meal Replacement/Supplement",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DOT0700": {
                "label": "Infant Formula",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "DOT0800": {
                "label": "Infant Formula - Nondairy",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FMF0100": {
                "label": "Margarine",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FMR0100": {
                "label": "Margarine - Reduced Fat",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FOF0100": {
                "label": "Vegetable Oils",
                "daily_values": [
                    1.391,
                    1.483,
                    0.0
                ],
                "value": 0.9580000000000001
            },
            "FSF0100": {
                "label": "Shortening",
                "daily_values": [
                    0.0,
                    0.545,
                    0.0
                ],
                "value": 0.1816666666666667
            },
            "FAF0100": {
                "label": "Butter",
                "daily_values": [
                    0.0,
                    1.0,
                    4.0
                ],
                "value": 1.666666666666667
            },
            "FAR0100": {
                "label": "Butter and Other Animal Fats - Reduced Fat",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FDF0100": {
                "label": "Creamy Dressings",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "FDR0100": {
                "label": "Salad Dressing - Reduced Fat/Reduced Calorie/Fat Free",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "SWT0400": {
                "label": "Sugar",
                "daily_values": [
                    0.25,
                    5.208,
                    5.208
                ],
                "value": 3.555333333333333
            },
            "MSC1200": {
                "label": "Sugar Substitute",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "SWT0500": {
                "label": "Syrup, Honey, Jam",
                "daily_values": [
                    0.455,
                    0.0,
                    0.0
                ],
                "value": 0.1516666666666667
            },
            "SWT0700": {
                "label": "Fudge, caramel, butterscotch, etc.",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "SWT0800": {
                "label": "Sauces, Sweet - Reduced Fat/Reduced Calorie/Fat Free",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "SWT0100": {
                "label": "Chocolate Candy",
                "daily_values": [
                    0.0,
                    0.4,
                    0.0
                ],
                "value": 0.1333333333333333
            },
            "SWT0200": {
                "label": "Candy",
                "daily_values": [
                    0.709,
                    0.0,
                    1.462
                ],
                "value": 0.7236666666666666
            },
            "SWT0300": {
                "label": "Frosting or icing",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVS0400": {
                "label": "Soda",
                "daily_values": [
                    3.0,
                    1.5,
                    3.0
                ],
                "value": 2.5
            },
            "BVA0400": {
                "label": "Diet Soda",
                "daily_values": [
                    0.0,
                    1.5,
                    0.0
                ],
                "value": 0.5
            },
            "BVU0300": {
                "label": "Unsweetened Soft Drinks",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVS0300": {
                "label": "Sweetened Fruit Drinks",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVA0300": {
                "label": "Artificially Sweetened Fruit Drinks",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVS0500": {
                "label": "Sweetened Tea",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVA0500": {
                "label": "Artificially Sweetened Tea",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVU0400": {
                "label": "Unsweetened Tea",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVS0100": {
                "label": "Sweetened Coffee",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVA0100": {
                "label": "Artificially Sweetened Coffee",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVU0100": {
                "label": "Unsweetened Coffee",
                "daily_values": [
                    0.455,
                    0.75,
                    0.75
                ],
                "value": 0.6516666666666667
            },
            "BVS0200": {
                "label": "Sweetened Coffee Substitutes",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVA0200": {
                "label": "Artificially Sweetened Coffee Substitutes",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVU0200": {
                "label": "Unsweetened Coffee Substitutes",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVS0600": {
                "label": "Sweetened Water",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVA0600": {
                "label": "Artificially Sweetened Water",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVU0500": {
                "label": "Water",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVS0700": {
                "label": "Nondairy-based Sweetened Meal Replacement/Supplement",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVA0700": {
                "label": "Nondairy-based Artificially Sweetened Meal Replacement/Supplement",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVU0600": {
                "label": "Nondairy-based Unsweetened Meal Replacement/Supplement",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVO0100": {
                "label": "Non-alcoholic Beer",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVO0200": {
                "label": "Non-alcoholic Light Beer",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVE0100": {
                "label": "Beer and Ales",
                "daily_values": [
                    0.0,
                    0.0,
                    1.0
                ],
                "value": 0.3333333333333333
            },
            "BVE0400": {
                "label": "Cordial and Liqueur",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVE0300": {
                "label": "Distilled Liquor",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "BVE0200": {
                "label": "Wine",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MSC0100": {
                "label": "Gravy",
                "daily_values": [
                    0.0,
                    0.5,
                    0.0
                ],
                "value": 0.1666666666666667
            },
            "MSC0200": {
                "label": "Reduced Fat Gravy",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MSC0300": {
                "label": "Fatty Sauces",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MSC0400": {
                "label": "Sweet/Salty Sauces",
                "daily_values": [
                    6.288,
                    0.0,
                    3.0
                ],
                "value": 3.096
            },
            "MSC0500": {
                "label": "Pickled Vegetables",
                "daily_values": [
                    0.233,
                    0.0,
                    0.0
                ],
                "value": 0.07766666666666668
            },
            "MSC0600": {
                "label": "Misc",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MSC0700": {
                "label": "Non-grain Flour and Similar",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MSC0800": {
                "label": "Soup Broth",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MSC0900": {
                "label": "Baby Food Dessert",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "MSC1000": {
                "label": "Miscellaneous Baby Food Mixtures",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRW1300": {
                "label": "Baby Food Grain Mixtures – Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "GRS1300": {
                "label": "Baby Food Grain Mixtures – Some Whole Grain",
                "daily_values": [
                    0.0,
                    0.0,
                    0.0
                ],
                "value": 0.0
            },
            "meta-fruit-juice": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "FRU0100": {
                        "id": "FRU0100",
                        "label": "Citrus Juice",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "FRU0200": {
                        "id": "FRU0200",
                        "label": "Fruit Juice excluding Citrus Juice",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Fruit Juice",
                "value": 0.0
            },
            "meta-fruit": {
                "include_subgroup": false,
                "suppress_increase": false,
                "suppress_decrease": true,
                "children": {
                    "FRU0300": {
                        "id": "FRU0300",
                        "label": "Citrus Fruit",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "value": 0.0
                    },
                    "FRU0400": {
                        "id": "FRU0400",
                        "label": "Fruit excluding Citrus Fruit",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Fruit",
                "value": 0.0
            },
            "meta-processed-fruits": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "FRU0600": {
                        "id": "FRU0600",
                        "label": "Fried Fruits",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "FRU0700": {
                        "id": "FRU0700",
                        "label": "Fruit-based Savory Snack",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Processed Fruits",
                "value": 0.0
            },
            "meta-vegetables": {
                "include_subgroup": false,
                "suppress_increase": false,
                "suppress_decrease": true,
                "children": {
                    "VEG0100": {
                        "id": "VEG0100",
                        "label": "Green Leafy Vegetables",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "value": 0.0
                    },
                    "VEG0200": {
                        "id": "VEG0200",
                        "label": "Deep-yellow Vegetables",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "VEG0300": {
                        "id": "VEG0300",
                        "label": "Tomato",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.2083333333333333
                    },
                    "VEG0400": {
                        "id": "VEG0400",
                        "label": "White Potatoes",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.1946666666666667
                    },
                    "VEG0450": {
                        "id": "VEG0450",
                        "label": "Other Starchy Vegetables",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "VEG0700": {
                        "id": "VEG0700",
                        "label": "Beans",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "VEG0600": {
                        "id": "VEG0600",
                        "label": "Other Vegetables",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.029
                    },
                    "MSC0500": {
                        "id": "MSC0500",
                        "label": "Pickled Vegetables",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.07766666666666668
                    }
                },
                "label": "Vegetables",
                "value": 0.5096666666666667
            },
            "meta-fried-vegetables": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "VEG0800": {
                        "id": "VEG0800",
                        "label": "French Fries",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.357
                    },
                    "VEG0900": {
                        "id": "VEG0900",
                        "label": "Fried Vegetables",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Fried Vegetables",
                "value": 0.357
            },
            "meta-vegetable-juice": {
                "include_subgroup": false,
                "suppress_increase": false,
                "suppress_decrease": true,
                "children": {
                    "VEG0500": {
                        "id": "VEG0500",
                        "label": "Vegetable Juice",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Vegetable Juice",
                "value": 0.0
            },
            "meta-beans-lentils": {
                "include_subgroup": false,
                "suppress_increase": false,
                "suppress_decrease": true,
                "children": {
                    "VEG0700": {
                        "id": "VEG0700",
                        "label": "Beans",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Beans & Lentils",
                "value": 0.0
            },
            "meta-nuts-seeds": {
                "include_subgroup": false,
                "suppress_increase": false,
                "suppress_decrease": true,
                "children": {
                    "MOF0500": {
                        "id": "MOF0500",
                        "label": "Nuts and Seeds",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "value": 0.0
                    },
                    "MOF0600": {
                        "id": "MOF0600",
                        "label": "Nut and Seed Butters",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "value": 0.0
                    }
                },
                "label": "Nuts & Seeds",
                "value": 0.0
            },
            "meta-whole-grains": {
                "include_subgroup": false,
                "suppress_increase": false,
                "suppress_decrease": true,
                "children": {
                    "GRW0100": {
                        "id": "GRW0100",
                        "label": "Grains, Flour and Dry Mixes - Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRW0200": {
                        "id": "GRW0200",
                        "label": "Loaf-type Bread and Plain Rolls - Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRW0300": {
                        "id": "GRW0300",
                        "label": "Other Breads (quick breads, corn muffins, tortillas) - Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRW0400": {
                        "id": "GRW0400",
                        "label": "Crackers - Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRW0500": {
                        "id": "GRW0500",
                        "label": "Pasta - Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRW0600": {
                        "id": "GRW0600",
                        "label": "Ready-to-eat Cereal (not presweetened) - Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRW1100": {
                        "id": "GRW1100",
                        "label": "Popcorn",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRW0700": {
                        "id": "GRW0700",
                        "label": "Ready-to-eat Cereal (presweetened) - Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRW0900": {
                        "id": "GRW0900",
                        "label": "Snack Chips - Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRW1000": {
                        "id": "GRW1000",
                        "label": "Snack Bars - Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Whole Grains",
                "value": 0.0
            },
            "meta-refined-grains": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "GRS0100": {
                        "id": "GRS0100",
                        "label": "Grains, Flour and Dry Mixes - Some Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRR0100": {
                        "id": "GRR0100",
                        "label": "Grains, Flour and Dry Mixes - Refined Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 1.176333333333333
                    },
                    "GRS0200": {
                        "id": "GRS0200",
                        "label": "Loaf-type Bread and Plain Rolls - Some Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRR0200": {
                        "id": "GRR0200",
                        "label": "Loaf-type Bread and Plain Rolls - Refined Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 2.341
                    },
                    "GRS0300": {
                        "id": "GRS0300",
                        "label": "Other Breads (quick breads, corn muffins, tortillas) - Some Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRR0300": {
                        "id": "GRR0300",
                        "label": "Other Breads (quick breads, corn muffins, tortillas) - Refined Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRS0400": {
                        "id": "GRS0400",
                        "label": "Crackers - Some Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRR0400": {
                        "id": "GRR0400",
                        "label": "Crackers - Refined Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRS0500": {
                        "id": "GRS0500",
                        "label": "Pasta - Some Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRR0500": {
                        "id": "GRR0500",
                        "label": "Pasta - Refined Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.381
                    },
                    "GRS0600": {
                        "id": "GRS0600",
                        "label": "Ready-to-eat Cereal (not presweetened) - Some Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRR0600": {
                        "id": "GRR0600",
                        "label": "Ready-to-eat Cereal (not presweetened) - Refined Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRS0700": {
                        "id": "GRS0700",
                        "label": "Ready-to-eat Cereal (presweetened) - Some Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRR0700": {
                        "id": "GRR0700",
                        "label": "Ready-to-eat Cereal (presweetened) - Refined Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRS0800": {
                        "id": "GRS0800",
                        "label": "Cakes, Cookies, Pies, Pastries, Danish, Doughnuts and Cobblers - Some Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRR0800": {
                        "id": "GRR0800",
                        "label": "Cakes, Cookies, Pies, Pastries, Danish, Doughnuts and Cobblers - Refined Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 2.432666666666667
                    },
                    "GRS1000": {
                        "id": "GRS1000",
                        "label": "Snack Bars - Some Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRR1000": {
                        "id": "GRR1000",
                        "label": "Snack Bars - Refined Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRS0900": {
                        "id": "GRS0900",
                        "label": "Snack Chips - Some Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRR0900": {
                        "id": "GRR0900",
                        "label": "Snack Chips - Refined Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Refined Grains",
                "value": 6.331
            },
            "meta-full-fat-dairy-products": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "DMF0100": {
                        "id": "DMF0100",
                        "label": "Whole Milk",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DMF0200": {
                        "id": "DMF0200",
                        "label": "Ready-to-drink Flavored Milk - Whole",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DCF0100": {
                        "id": "DCF0100",
                        "label": "Cheese",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.1646666666666667
                    },
                    "DYF0100": {
                        "id": "DYF0100",
                        "label": "Yogurt - Sweetened Whole Milk",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DYF0200": {
                        "id": "DYF0200",
                        "label": "Yogurt - Artificially Sweetened Whole Milk",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "FCF0100": {
                        "id": "FCF0100",
                        "label": "Cream",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.2423333333333333
                    }
                },
                "label": "Full-Fat Dairy Products",
                "value": 0.407
            },
            "meta-reduced-or-non-fat-dairy-products": {
                "include_subgroup": false,
                "suppress_increase": false,
                "suppress_decrease": true,
                "children": {
                    "DMR0100": {
                        "id": "DMR0100",
                        "label": "Milk - Reduced Fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DMR0200": {
                        "id": "DMR0200",
                        "label": "Ready-to-drink Flavored Milk - Reduced Fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DCR0100": {
                        "id": "DCR0100",
                        "label": "Cheese - Reduced Fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.3606666666666667
                    },
                    "DYR0100": {
                        "id": "DYR0100",
                        "label": "Yogurt - Sweetened Low Fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DYR0200": {
                        "id": "DYR0200",
                        "label": "Yogurt - Artificially Sweetened Low Fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "FCR0100": {
                        "id": "FCR0100",
                        "label": "Cream - Reduced Fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DML0100": {
                        "id": "DML0100",
                        "label": "Milk - Low Fat and Fat Free",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DML0200": {
                        "id": "DML0200",
                        "label": "Ready-to-drink Flavored Milk - Low Fat and Fat Free",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DCL0100": {
                        "id": "DCL0100",
                        "label": "Cheese - Low Fat and Fat Free",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DYL0100": {
                        "id": "DYL0100",
                        "label": "Yogurt - Sweetened Fat Free",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DYL0200": {
                        "id": "DYL0200",
                        "label": "Yogurt - Artificially Sweetened Fat Free",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "FCL0100": {
                        "id": "FCL0100",
                        "label": "Cream - Low Fat and Fat Free",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Reduced or Non-Fat Dairy Products",
                "value": 0.3606666666666667
            },
            "meta-dairy-based-desserts": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "DOT0100": {
                        "id": "DOT0100",
                        "label": "Frozen Dairy Dessert",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DOT0300": {
                        "id": "DOT0300",
                        "label": "Pudding and Other Dairy Dessert",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DOT0400": {
                        "id": "DOT0400",
                        "label": "Artificially Sweetened Pudding and Other Dairy Dessert",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Dairy-Based Desserts",
                "value": 0.0
            },
            "meta-plant-based-dairy-alternatives": {
                "include_subgroup": false,
                "suppress_increase": false,
                "suppress_decrease": true,
                "children": {
                    "DMN0100": {
                        "id": "DMN0100",
                        "label": "Milk - Nondairy",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.006333333333333333
                    },
                    "DCN0100": {
                        "id": "DCN0100",
                        "label": "Cheese - Nondairy",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DYN0100": {
                        "id": "DYN0100",
                        "label": "Yogurt - Nondairy",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DOT0200": {
                        "id": "DOT0200",
                        "label": "Frozen Nondairy Dessert",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.2913333333333333
                    },
                    "FCN0100": {
                        "id": "FCN0100",
                        "label": "Cream - Nondairy, Soy, rice, grain based",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 2.666666666666667
                    }
                },
                "label": "Plant-Based Dairy Alternatives",
                "value": 2.964333333333333
            },
            "meta-processed-meat": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "MCF0100": {
                        "id": "MCF0100",
                        "label": "Cold Cuts and Sausage",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 1.723
                    },
                    "MCL0100": {
                        "id": "MCL0100",
                        "label": "Lean Cold Cuts and Sausage",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.06666666666666667
                    },
                    "MCF0200": {
                        "id": "MCF0200",
                        "label": "Cured Pork",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.2546666666666667
                    },
                    "MCL0200": {
                        "id": "MCL0200",
                        "label": "Lean Cured Pork",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "FMC0200": {
                        "id": "FMC0200",
                        "label": "Meat-based Savory Snack",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Processed Meat",
                "value": 2.044333333333333
            },
            "meta-red-meat": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "MRF0100": {
                        "id": "MRF0100",
                        "label": "Beef",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.9756666666666667
                    },
                    "MRL0100": {
                        "id": "MRL0100",
                        "label": "Lean Beef",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MRF0200": {
                        "id": "MRF0200",
                        "label": "Veal",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MRL0200": {
                        "id": "MRL0200",
                        "label": "Lean Veal",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MRF0300": {
                        "id": "MRF0300",
                        "label": "Lamb",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MRL0300": {
                        "id": "MRL0300",
                        "label": "Lean Lamb",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MRF0400": {
                        "id": "MRF0400",
                        "label": "Fresh Pork",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MRL0400": {
                        "id": "MRL0400",
                        "label": "Lean Fresh Pork",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MRF0500": {
                        "id": "MRF0500",
                        "label": "Game",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MOF0100": {
                        "id": "MOF0100",
                        "label": "Organ Meats",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Red Meat",
                "value": 0.9756666666666667
            },
            "meta-poultry": {
                "include_subgroup": false,
                "suppress_increase": false,
                "suppress_decrease": false,
                "children": {
                    "MPF0100": {
                        "id": "MPF0100",
                        "label": "Poultry",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MPL0100": {
                        "id": "MPL0100",
                        "label": "Lean Poultry",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MPF0200": {
                        "id": "MPF0200",
                        "label": "Fried Chicken",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 1.27
                    }
                },
                "label": "Poultry",
                "value": 1.27
            },
            "meta-fish": {
                "include_subgroup": false,
                "suppress_increase": false,
                "suppress_decrease": false,
                "children": {
                    "MFF0100": {
                        "id": "MFF0100",
                        "label": "Fish",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MFL0100": {
                        "id": "MFL0100",
                        "label": "Lean Fish",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MFF0200": {
                        "id": "MFF0200",
                        "label": "Fried Fish",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Fish",
                "value": 0.0
            },
            "meta-shellfish": {
                "include_subgroup": false,
                "suppress_increase": false,
                "suppress_decrease": false,
                "children": {
                    "MSL0100": {
                        "id": "MSL0100",
                        "label": "Shellfish",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MSF0100": {
                        "id": "MSF0100",
                        "label": "Fried Shellfish",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Shellfish",
                "value": 0.0
            },
            "meta-eggs": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "MOF0300": {
                        "id": "MOF0300",
                        "label": "Eggs",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MOF0400": {
                        "id": "MOF0400",
                        "label": "Egg Substitute",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Eggs",
                "value": 0.0
            },
            "meta-plant-based-meat-alternatives": {
                "include_subgroup": false,
                "suppress_increase": false,
                "suppress_decrease": true,
                "children": {
                    "MOF0700": {
                        "id": "MOF0700",
                        "label": "Meat Alternatives",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.005999999999999999
                    }
                },
                "label": "Plant-Based Meat Alternatives",
                "value": 0.005999999999999999
            },
            "meta-saturated-unhealthy-fats": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "FMF0100": {
                        "id": "FMF0100",
                        "label": "Margarine",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "FMR0100": {
                        "id": "FMR0100",
                        "label": "Margarine - Reduced Fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "FSF0100": {
                        "id": "FSF0100",
                        "label": "Shortening",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.1816666666666667
                    },
                    "FAF0100": {
                        "id": "FAF0100",
                        "label": "Butter",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 1.666666666666667
                    },
                    "FAR0100": {
                        "id": "FAR0100",
                        "label": "Butter and Other Animal Fats - Reduced Fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "FDF0100": {
                        "id": "FDF0100",
                        "label": "Creamy Dressings",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "FCF0100": {
                        "id": "FCF0100",
                        "label": "Cream",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.2423333333333333
                    }
                },
                "label": "Saturated (Unhealthy) Fats",
                "value": 2.090666666666667
            },
            "meta-unsaturated-healthier-fats": {
                "include_subgroup": false,
                "suppress_increase": false,
                "suppress_decrease": true,
                "children": {
                    "FOF0100": {
                        "id": "FOF0100",
                        "label": "Vegetable Oils",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.9580000000000001
                    },
                    "FRU0500": {
                        "id": "FRU0500",
                        "label": "Avocados",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "value": 0.0
                    }
                },
                "label": "Unsaturated (Healthier) Fats",
                "value": 0.9580000000000001
            },
            "meta-sweetened-beverages": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "BVS0400": {
                        "id": "BVS0400",
                        "label": "Soda",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 2.5
                    },
                    "BVS0300": {
                        "id": "BVS0300",
                        "label": "Sweetened Fruit Drinks",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "BVS0500": {
                        "id": "BVS0500",
                        "label": "Sweetened Tea",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "BVS0100": {
                        "id": "BVS0100",
                        "label": "Sweetened Coffee",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "BVS0200": {
                        "id": "BVS0200",
                        "label": "Sweetened Coffee Substitutes",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "BVS0600": {
                        "id": "BVS0600",
                        "label": "Sweetened Water",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "BVO0100": {
                        "id": "BVO0100",
                        "label": "Non-alcoholic Beer",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "BVO0200": {
                        "id": "BVO0200",
                        "label": "Non-alcoholic Light Beer",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Sweetened Beverages",
                "value": 2.5
            },
            "meta-artificially-sweetened-beverages": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "BVA0500": {
                        "id": "BVA0500",
                        "label": "Artificially Sweetened Tea",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "BVA0100": {
                        "id": "BVA0100",
                        "label": "Artificially Sweetened Coffee",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "BVA0200": {
                        "id": "BVA0200",
                        "label": "Artificially Sweetened Coffee Substitutes",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "BVA0600": {
                        "id": "BVA0600",
                        "label": "Artificially Sweetened Water",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "BVA0400": {
                        "id": "BVA0400",
                        "label": "Diet Soda",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.5
                    },
                    "BVA0300": {
                        "id": "BVA0300",
                        "label": "Artificially Sweetened Fruit Drinks",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Artificially Sweetened Beverages",
                "value": 0.5
            },
            "meta-unsweetened-beverages": {
                "include_subgroup": false,
                "suppress_increase": false,
                "suppress_decrease": true,
                "children": {
                    "BVU0500": {
                        "id": "BVU0500",
                        "label": "Water",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "value": 0.0
                    },
                    "BVU0300": {
                        "id": "BVU0300",
                        "label": "Unsweetened Soft Drinks",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "BVU0400": {
                        "id": "BVU0400",
                        "label": "Unsweetened Tea",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "BVU0100": {
                        "id": "BVU0100",
                        "label": "Unsweetened Coffee",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.6516666666666667
                    },
                    "BVU0200": {
                        "id": "BVU0200",
                        "label": "Unsweetened Coffee Substitutes",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Unsweetened Beverages",
                "value": 0.6516666666666667
            },
            "meta-alcoholic-beverages": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "BVE0100": {
                        "id": "BVE0100",
                        "label": "Beer and Ales",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.3333333333333333
                    },
                    "BVE0400": {
                        "id": "BVE0400",
                        "label": "Cordial and Liqueur",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "BVE0300": {
                        "id": "BVE0300",
                        "label": "Distilled Liquor",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "BVE0200": {
                        "id": "BVE0200",
                        "label": "Wine",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Alcoholic Beverages",
                "value": 0.3333333333333333
            },
            "meta-fried-foods": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "MSF0100": {
                        "id": "MSF0100",
                        "label": "Fried Shellfish",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MFF0200": {
                        "id": "MFF0200",
                        "label": "Fried Fish",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "FMC0100": {
                        "id": "FMC0100",
                        "label": "Vegetable-based Savory Snack",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "VEG0800": {
                        "id": "VEG0800",
                        "label": "French Fries",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.357
                    },
                    "VEG0900": {
                        "id": "VEG0900",
                        "label": "Fried Vegetables",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "FRU0600": {
                        "id": "FRU0600",
                        "label": "Fried Fruits",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MPF0200": {
                        "id": "MPF0200",
                        "label": "Fried Chicken",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 1.27
                    }
                },
                "label": "Fried Foods",
                "value": 1.627
            },
            "meta-salty-snacks": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "GRS0900": {
                        "id": "GRS0900",
                        "label": "Snack Chips Some Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRR0900": {
                        "id": "GRR0900",
                        "label": "Snack Chips",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRW1200": {
                        "id": "GRW1200",
                        "label": "Flavored Popcorn",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "FMC0100": {
                        "id": "FMC0100",
                        "label": "Vegetable Based Savory Snack (Potato Chips)",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "FMC0200": {
                        "id": "FMC0200",
                        "label": "Meat Based Savory Snack (Pork Rinds)",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Salty Snacks",
                "value": 0.0
            },
            "meta-sweets-desserts": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "MSC0600": {
                        "id": "MSC0600",
                        "label": "Misc",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DOT0100": {
                        "id": "DOT0100",
                        "label": "Frozen Dairy Dessert",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DOT0200": {
                        "id": "DOT0200",
                        "label": "Frozen Nondairy Dessert",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.2913333333333333
                    },
                    "DOT0300": {
                        "id": "DOT0300",
                        "label": "Pudding and Other Dairy Dessert",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "DOT0400": {
                        "id": "DOT0400",
                        "label": "Artificially Sweetened Pudding and Other Dairy Dessert",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRW0800": {
                        "id": "GRW0800",
                        "label": "Whole Grain Cookies, Pies, Pastries, Danish, etc.",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRS0800": {
                        "id": "GRS0800",
                        "label": "Some Whole Grain Cookies, Pies, Pastries, Danish, etc.",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRR0800": {
                        "id": "GRR0800",
                        "label": "Cookies, Pies, Pastries, Danish, etc.",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 2.432666666666667
                    },
                    "GRS1000": {
                        "id": "GRS1000",
                        "label": "Snack Bars Some Whole Grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRR1000": {
                        "id": "GRR1000",
                        "label": "Snack Bars",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRR0300": {
                        "id": "GRR0300",
                        "label": "Refined Snack Breads",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "SWT0500": {
                        "id": "SWT0500",
                        "label": "Syrup, Honey, Jam",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.1516666666666667
                    },
                    "SWT0400": {
                        "id": "SWT0400",
                        "label": "Sugar",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 3.555333333333333
                    },
                    "SWT0700": {
                        "id": "SWT0700",
                        "label": "Fudge, caramel, etc.",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "SWT0100": {
                        "id": "SWT0100",
                        "label": "Chocolate Candy",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.1333333333333333
                    },
                    "SWT0200": {
                        "id": "SWT0200",
                        "label": "Candy",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.7236666666666666
                    },
                    "SWT0300": {
                        "id": "SWT0300",
                        "label": "Frosting or icing",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Sweets & Desserts",
                "value": 7.288
            },
            "meta-healthier-snacks": {
                "include_subgroup": false,
                "suppress_increase": false,
                "suppress_decrease": true,
                "children": {
                    "GRW1000": {
                        "id": "GRW1000",
                        "label": "Whole Grain Snack Bars",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRW0900": {
                        "id": "GRW0900",
                        "label": "Whole Grain Snack Chips",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRW1100": {
                        "id": "GRW1100",
                        "label": "Plain Popcorn",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "GRW0300": {
                        "id": "GRW0300",
                        "label": "Quick and Other Breads",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Healthier Snacks",
                "value": 0.0
            },
            "meta-sweet-salty-condiments": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "MSC0400": {
                        "id": "MSC0400",
                        "label": "Sweet/Salty Sauces",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 3.096
                    },
                    "SWT0500": {
                        "id": "SWT0500",
                        "label": "Syrup, Honey, Jam",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.1516666666666667
                    },
                    "SWT0400": {
                        "id": "SWT0400",
                        "label": "Sugar",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 3.555333333333333
                    },
                    "SWT0700": {
                        "id": "SWT0700",
                        "label": "Fudge, caramel, butterscotch, etc.",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "SWT0300": {
                        "id": "SWT0300",
                        "label": "Frosting or icing",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Sweet / Salty Condiments",
                "value": 6.803000000000001
            },
            "meta-fatty-condiments": {
                "include_subgroup": false,
                "suppress_increase": true,
                "suppress_decrease": false,
                "children": {
                    "MSC0100": {
                        "id": "MSC0100",
                        "label": "Gravy",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.1666666666666667
                    },
                    "MSC0200": {
                        "id": "MSC0200",
                        "label": "Reduced Fat Gravy",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "MSC0300": {
                        "id": "MSC0300",
                        "label": "Fatty Sauces",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0
                    },
                    "FCF0100": {
                        "id": "FCF0100",
                        "label": "Cream",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.2423333333333333
                    },
                    "FDF0100": {
                        "id": "FDF0100",
                        "label": "Creamy Dressings",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.0
                    }
                },
                "label": "Fatty Condiments",
                "value": 0.409
            }
        },
        "active": true
    }
}`}
            />
          </Grid>
          <Grid
            item
            xs={2}
          >
            <h3>Error Response</h3>
          </Grid>
          <Grid
            item
            xs={10}
          >
            <CodeHighlighter
              code={`{
    "success": "false",
    "message": "Must be logged in"
}
  `}
            />
          </Grid>
        </Grid>
      </Typography>
    </AccordionDetails>
  );
};

export default DietsDietCodeQuality;
